import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Decode the token to get user data
        // In production, this would be a proper JWT decode
        // For now, we'll parse our mock token format
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUser(payload);
        } catch (e) {
          // Fallback for our mock token format
          const userData = {
            id: '1',
            name: token === 'admin@admin.com-admin123' ? 'Admin User' : 'Regular User',
            email: token.split('-')[0],
            role: token === 'admin@admin.com-admin123' ? 'admin' : 'user'
          };
          setUser(userData);
        }
      }
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    // Mock authentication for development
    if ((email === 'admin@admin.com' && password === 'admin123') || 
        (email === 'user@user.com' && password === 'user123')) {
      
      const userData = email === 'admin@admin.com' 
        ? { id: '1', name: 'Admin User', email: 'admin@admin.com', role: 'admin' }
        : { id: '2', name: 'Regular User', email: 'user@user.com', role: 'user' };
      
      // Create a proper JWT-like token for better compatibility
      const header = btoa(JSON.stringify({alg: 'HS256', typ: 'JWT'}));
      const payload = btoa(JSON.stringify(userData));
      const signature = 'mock-signature';
      const token = `${header}.${payload}.${signature}`;
      
      localStorage.setItem('token', token);
      setUser(userData);
      return { user: userData, token };
    } else {
      throw new Error('Invalid credentials');
    }
  };

  const register = async (email, password, name) => {
    // Mock registration for development
    const userData = { id: Date.now().toString(), name, email, role: 'user' };
    
    // Create a proper JWT-like token
    const header = btoa(JSON.stringify({alg: 'HS256', typ: 'JWT'}));
    const payload = btoa(JSON.stringify(userData));
    const signature = 'mock-signature';
    const token = `${header}.${payload}.${signature}`;
    
    localStorage.setItem('token', token);
    setUser(userData);
    return { user: userData, token };
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    checkAuthStatus
  };

  // Only render children when not loading
  // This prevents components from rendering before auth state is determined
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};